import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, CircularProgress } from "@mui/material";
import Typography from "@mui/material/Typography";
import ArrowIcon from "assets/icons/arrowIcon.svg";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import AccordionSummary from "@mui/material/AccordionSummary";
import IOSSwitch from "components/Elements/IOSSwitch";
import { updateSiteDetails } from "services/clients/sites/siteDetails";
import { showError } from "../../../../../redux/common/actions";

const useStyles = makeStyles()(() => ({
	detailContainer: {
		marginTop: "25px !important",
		borderColor: ColourConstants.commonBorder,
		borderStyle: "solid",
		borderWidth: 1,
		width: "100%",
	},
	expandIcon: {
		transform: "scale(0.8)",
	},
	sectionHeading: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: "17px",
	},
	summary: {
		alignItems: "center",
		justifyContent: "space-between",
		marginRight: "10px !important",
		marginTop: 0,
		marginBottom: 0,
	},
}));

function FeedbackRequireDepartment({
	accordianDetailsCss,
	noExpand,
	defaultExpanded = true,
	feedbackRequireDepartment,
	siteId,
	isLoading,
}) {
	const { classes } = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	const [isMouseOverSwitch, setIsMouseOverSwitch] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isActive, setIsActive] = useState(feedbackRequireDepartment);

	const onToggle = async (e) => {
		e.stopPropagation();
		setIsUpdating(true);
		try {
			const response = await updateSiteDetails(siteId, {
				op: "replace",
				label: "feedbackRequireDepartment",
				value: !isActive,
			});
			if (response.status) {
				setIsActive(response?.data?.feedbackRequireDepartment ?? !isActive);
			} else {
				dispatch(showError(t("message.failedToUpdateFeedbackRequireDepartment")));
			}
		} catch (error) {
			dispatch(showError(t("message.failedToUpdateFeedbackRequireDepartment")));
		} finally {
			setIsUpdating(false);
		}
	};

	useEffect(() => {
		setIsActive(feedbackRequireDepartment);
	}, [feedbackRequireDepartment]);

	return (
		<Accordion
			className={classes.detailContainer}
			expanded={isExpanded}
			onChange={() => {
				if (isMouseOverSwitch) {
					setIsExpanded((prev) => prev);
				} else {
					setIsExpanded(!isExpanded);
				}
			}}
			style={{ padding: 0 }}
		>
			<AccordionSummary
				expandIcon={
					noExpand ? null : (
						<img
							alt="Expand icon"
							src={ArrowIcon}
							className={classes.expandIcon}
						/>
					)
				}
				aria-controls="feedback-require-department-content"
				id="feedback-require-department-header"
				classes={{ content: classes.summary }}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<Typography className={classes.sectionHeading}>
						{t("title.feedbackRequireDepartment")}
					</Typography>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							flexWrap: "wrap",
							gap: "10px",
							alignItems: "center",
						}}
					>
						<div
							onMouseEnter={() => setIsMouseOverSwitch(true)}
							onMouseLeave={() => setIsMouseOverSwitch(false)}
						>
							<IOSSwitch
								color={true}
								onChange={onToggle}
								currentStatus={isActive}
								disabled={isUpdating || isLoading}
							/>
						</div>
					</div>
				</div>
			</AccordionSummary>
			<AccordionDetails className={accordianDetailsCss}>
				{isLoading ? (
					<CircularProgress />
				) : (
					<Typography>
						{t("message.feedbackRequireDepartmentDescription")}
					</Typography>
				)}
			</AccordionDetails>
		</Accordion>
	);
}

export default FeedbackRequireDepartment;
